import React from 'react';
import dayjs from 'shared/helpers/dayjs';

import { General, Shapes, Time } from 'icons/untitledui';
import colours from 'theme/atoms/colours';
import * as SearchModalStyles from 'theme/components/SearchModal.style';
import { OPEN_COLOUR, RESOLVED_COLOUR } from 'events/styles/DaySummary.style';
import type { Event, EventStatus, EventTimelineNote } from 'events/_types';

const STATUS_ICONS: Record<
  EventStatus,
  React.ComponentType<{ size?: number }>
> = {
  open: Shapes.Circle,
  resolved: General.CheckCircle,
  auto_resolved: Time.ClockCheck,
  invisible: General.EyeOff,
};

const STATUS_COLORS: Record<EventStatus, string> = {
  open: OPEN_COLOUR,
  resolved: RESOLVED_COLOUR,
  auto_resolved: RESOLVED_COLOUR,
  invisible: colours.neutral[500],
};

interface EventGroupCardProps {
  event: Event;
  notes: EventTimelineNote[];
  latestCreated?: string;
  dateFormat?: string;
  emptyTitleFallback?: string;
  emptyNoteFallback?: string;
  onEventClick: (event: Event) => void;
  onNoteClick?: (event: Event, note: EventTimelineNote) => void;
  renderEventTitle?: (title: string) => React.ReactNode;
  renderEventSummary?: (summary: string) => React.ReactNode;
  renderNoteText?: (note: EventTimelineNote) => React.ReactNode;
  cardRef?: (el: HTMLElement | null) => void;
  onMouseEnter?: () => void;
}

export default function EventGroupCard({
  event,
  notes,
  latestCreated,
  dateFormat = 'MMMM D, YYYY',
  emptyTitleFallback = '',
  emptyNoteFallback = '',
  onEventClick,
  onNoteClick,
  renderEventTitle,
  renderEventSummary,
  renderNoteText,
  cardRef,
  onMouseEnter,
}: EventGroupCardProps): JSX.Element {
  const status = (event.status as EventStatus) || 'open';
  const StatusIconComponent = event.is_private
    ? General.EyeOff
    : STATUS_ICONS[status];
  const statusColor = event.is_private
    ? colours.neutral[500]
    : STATUS_COLORS[status];

  const dateSource = latestCreated ?? event.modified ?? event.created;
  const parsedDate = dayjs(dateSource);
  const dateLabel = parsedDate.isValid() ? parsedDate.format(dateFormat) : '';

  const title = event.title || '';
  const titleNode = renderEventTitle
    ? renderEventTitle(title)
    : title || emptyTitleFallback;
  const summaryNode = renderEventSummary
    ? renderEventSummary(event.summary || '')
    : event.summary || null;

  return (
    <SearchModalStyles.GroupCard
      ref={cardRef}
      role="button"
      tabIndex={0}
      onMouseEnter={onMouseEnter}
      onClick={() => onEventClick(event)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEventClick(event);
        }
      }}
    >
      <SearchModalStyles.EventBlock>
        <SearchModalStyles.EventHeaderRow>
          <SearchModalStyles.EventTitleGroup>
            <SearchModalStyles.StatusIcon
              $color={statusColor}
              aria-hidden="true"
            >
              <StatusIconComponent size={16} />
            </SearchModalStyles.StatusIcon>
            <SearchModalStyles.EventTitleText>
              {titleNode}
            </SearchModalStyles.EventTitleText>
          </SearchModalStyles.EventTitleGroup>
          {dateLabel && (
            <SearchModalStyles.EventDate>
              {dateLabel}
            </SearchModalStyles.EventDate>
          )}
        </SearchModalStyles.EventHeaderRow>
        {summaryNode && (
          <SearchModalStyles.EventSummaryText>
            {summaryNode}
          </SearchModalStyles.EventSummaryText>
        )}
      </SearchModalStyles.EventBlock>
      {notes.length > 0 && (
        <SearchModalStyles.NotesList>
          {notes.map((note) => {
            const noteNode = renderNoteText
              ? renderNoteText(note)
              : note.edited_text || emptyNoteFallback;
            return (
              <SearchModalStyles.NoteBlock
                key={note.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNoteClick?.(event, note);
                }}
              >
                <SearchModalStyles.NoteText>
                  {noteNode}
                </SearchModalStyles.NoteText>
              </SearchModalStyles.NoteBlock>
            );
          })}
        </SearchModalStyles.NotesList>
      )}
    </SearchModalStyles.GroupCard>
  );
}
